<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { useRedirect } from "@core/composables"

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Item de breadcrumb
 */
export interface BreadcrumbItem {
    /**
     * Label à afficher
     */
    label: string
    /**
     * Icône PrimeIcons (ex: "pi pi-home")
     */
    icon?: string
    /**
     * Route de navigation (optionnel)
     */
    route?: string
}

type Props = {
    /**
     * Titre de la page
     */
    title: string
    /**
     * Liste des items du breadcrumb
     */
    breadcrumb?: BreadcrumbItem[]
    /**
     * Classes CSS additionnelles pour le header
     */
    classHeader?: string
    /**
     * Couleur du titre (h1) ; `text-blue-700` par défaut, `text-surface-800`
     * au gabarit du design system
     */
    classTitle?: string
}

// ================================
// PROPS
// ================================
withDefaults(defineProps<Props>(), {
    breadcrumb: () => [],
    classHeader: "",
    classTitle: "text-blue-700",
})

// ================================
// COMPOSABLES
// ================================
const { redirectWrapper } = useRedirect()

// ================================
// INTERACTIONS UTILISATEUR
// ================================
/**
 * Gère le clic sur un élément du breadcrumb (accueil : `redirectWrapper('/')`).
 * @param route - La route où naviguer
 */
function onClickNavigate(route: string): void {
    redirectWrapper(route)
}
</script>

<template>
    <!-- ================================ -->
    <!-- HEADER PAGE -->
    <!-- ================================ -->
    <div class="header-page flex flex-col gap-4">
        <!-- ================================ -->
        <!-- BREADCRUMB -->
        <!-- ================================ -->
        <slot name="breadcrumb">
            <BaseBreadCrumb
                v-if="breadcrumb && breadcrumb.length > 0"
                :model="breadcrumb"
                :home="null"
                :pt="{ root: { 'aria-label': 'Fil d\'Ariane' } }"
                class="breadcrumb-page text-sm text-surface-600"
            >
                <template #item="{ item, props: propsItem }">
                    <div class="flex items-center gap-2">
                        <i
                            class="p-breadcrumb-item-icon"
                            v-if="item.icon"
                            :class="item.icon"
                        />
                        <button
                            v-if="item.route"
                            type="button"
                            v-bind="propsItem.action"
                            @click.prevent="onClickNavigate(item.route)"
                            class="cursor-pointer hover:text-primary"
                        >
                            <span class="hover:underline">{{
                                item.label
                            }}</span>
                        </button>
                        <p
                            v-else
                            :href="item.url"
                            :target="item.target"
                            v-bind="propsItem.action"
                        >
                            <span class="">{{ item.label }}</span>
                        </p>
                    </div>
                </template>
            </BaseBreadCrumb>
        </slot>

        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <header :class="classHeader">
            <div class="header-page__title-group flex min-w-0 flex-col gap-2">
                <h1 class="text-2xl font-semibold" :class="classTitle">
                    {{ title }}
                </h1>
                <div v-if="$slots.subtitle" class="header-page__subtitle">
                    <slot name="subtitle" />
                </div>
            </div>
            <slot></slot>
        </header>
    </div>
</template>

<style scoped>
/* ================================ */
/* COMPOSANT PRINCIPAL */
/* ================================ */
.header-page {
    width: 100%;
}

/* ================================ */
/* BREADCRUMB */
/* ================================ */
.breadcrumb-page {
    width: 100%;
}
</style>
